/**
 * @file column-widths.ts
 * @description Ширины и свёртка боковых колонок конструктора (phase19.16,
 * партия после приёмки Р2а; uiux-builder-shell.md §2, §9 — пороги и роли).
 *
 * Чистая логика без React: умолчания по порогам окна, границы колонок,
 * память (`localStorage`, ключ `commora.builder.columns`), «ужимание» колонок,
 * когда окну не хватает места под канву (сначала панель до своего минимума,
 * потом навигатор), и шаблон CSS-сетки.
 *
 * Колонки:
 *  - `nav` — навигатор от 1280 (240–400, умолчание 280, с 1600 — 300);
 *  - `panel` — панель настроек от 1280 (320–560, умолчание 380, с 1600 — 400);
 *  - `side` — левая колонка 768–1279 (навигатор или панель; 320–480,
 *    умолчание 320, с 1024 — 360).
 * Свёрнутая колонка — полоска 48 px без ручки; ширина на время свёртки
 * помнится. В памяти только то, что меняли руками: несохранённая ширина
 * берёт умолчание порога, сохранённая важнее порога; нет флага — развёрнута.
 */

/** Колонка, ширину которой можно менять. */
export type BuilderColumnKey = "nav" | "panel" | "side";

/** Раскладка, у которой есть ручки (на телефоне ручек нет). */
export type BuilderColumnsMode = "desktop" | "tablet";

/** Флаг свёртки колонки в памяти: `navCollapsed`, `panelCollapsed`, `sideCollapsed`. */
export type CollapsedFlag = `${BuilderColumnKey}Collapsed`;

/** Память: ширины колонок, которые меняли руками, и свёрнутые колонки. */
export type StoredColumns = Partial<Record<BuilderColumnKey, number>> & Partial<Record<CollapsedFlag, true>>;

/** Колонка сейчас: ширина на экране, границы ручки, свёрнута ли. */
export interface ResolvedColumn {
  width: number;
  min: number;
  max: number;
  collapsed: boolean;
}

/** Ключ памяти в `localStorage` (на устройстве сотрудника, для всех магазинов). */
export const COLUMNS_STORAGE_KEY = "commora.builder.columns";

/** Ширина ручки-разделителя в сетке, px. */
export const HANDLE_PX = 8;

/** Ширина свёрнутой колонки (полоска с кнопкой «Развернуть»), px. */
export const COLLAPSED_PX = 48;

/** Насколько протянуть ручку за минимум, чтобы колонка свернулась, px. */
export const COLLAPSE_PULL_PX = 40;

/** Шаг клавиатуры (стрелки ←/→), px. */
export const KEYBOARD_STEP_PX = 16;

/** Границы колонок, px. */
export const COLUMN_LIMITS: Record<BuilderColumnKey, { min: number; max: number }> = {
  nav: { min: 240, max: 400 },
  panel: { min: 320, max: 560 },
  side: { min: 320, max: 480 },
};

/**
 * Канве — не меньше, px. От 1280 — 600: на 1280 без памяти остаются прежние
 * 280/380 (панели спроектированы под 380; 1280 − 280 − 380 − 16 = 604).
 * На планшете канва меньше: при 768 левая колонка может вырасти до 400,
 * на канве остаётся ширина телефона.
 */
export const CANVAS_MIN: Record<BuilderColumnsMode, number> = { desktop: 600, tablet: 360 };

/** Пороги окна, от которых зависят умолчания. */
export interface WidthThresholds {
  /** Окно от 1600. */
  xl: boolean;
  /** Окно от 1024. */
  lg: boolean;
}

/** Умолчание колонки по порогу окна (как было до ручек). */
export function defaultColumnWidth(key: BuilderColumnKey, t: WidthThresholds): number {
  if (key === "nav") return t.xl ? 300 : 280;
  if (key === "panel") return t.xl ? 400 : 380;
  return t.lg ? 360 : 320;
}

/** Ширина в границах колонки (целые пиксели). */
export function clampColumn(key: BuilderColumnKey, px: number): number {
  const { min, max } = COLUMN_LIMITS[key];
  return Math.min(max, Math.max(min, Math.round(px)));
}

/** Имя флага свёртки колонки. */
export function collapsedFlag(key: BuilderColumnKey): CollapsedFlag {
  return `${key}Collapsed`;
}

const KEYS: readonly BuilderColumnKey[] = ["nav", "panel", "side"];

/** Разбор памяти: кривой JSON или значения вне границ — как будто их нет. */
export function parseStoredColumns(raw: string | null): StoredColumns {
  if (!raw) return {};
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof data !== "object" || data === null || Array.isArray(data)) return {};
  const src = data as Record<string, unknown>;
  const out: StoredColumns = {};
  for (const key of KEYS) {
    const value = src[key];
    const { min, max } = COLUMN_LIMITS[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= min && value <= max) out[key] = Math.round(value);
    // Флаг — только `true`; всё остальное (и старая память без флагов) — развёрнута.
    if (src[collapsedFlag(key)] === true) out[collapsedFlag(key)] = true;
  }
  return out;
}

/** Чтение памяти; недоступное хранилище (приватный режим, запрет) — пусто. */
export function readStoredColumns(): StoredColumns {
  try {
    return parseStoredColumns(window.localStorage.getItem(COLUMNS_STORAGE_KEY));
  } catch {
    return {};
  }
}

/** Запись памяти; пустая память удаляет ключ. Ошибки хранилища не мешают работе. */
export function writeStoredColumns(stored: StoredColumns): void {
  try {
    if (Object.keys(stored).length === 0) window.localStorage.removeItem(COLUMNS_STORAGE_KEY);
    else window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Память не записалась — ширина живёт до перезагрузки, это не ошибка для сотрудника.
  }
}

/** Что известно о месте под колонки. */
export interface ResolveInput {
  mode: BuilderColumnsMode;
  /** Есть ли правая панель (в режиме «на место канвы» её нет). */
  hasPanel: boolean;
  stored: StoredColumns;
  thresholds: WidthThresholds;
  /** Ширина контейнера сетки; `null` — ещё не измерена (не ужимаем). */
  containerWidth: number | null;
}

/** Колонки раскладки с итоговыми ширинами и границами ручек. */
export type ResolvedColumns = Partial<Record<BuilderColumnKey, ResolvedColumn>>;

/**
 * Итоговые ширины. Окну не хватает места под канву — сначала ужимается
 * панель до своего минимума, потом навигатор; память при этом не меняется
 * (окно расширили — колонки вернулись). Максимум ручки — сколько можно
 * взять, не залезая в минимум канвы и ужав соседку до её минимума
 * (`neighborFor`), но не меньше текущей ширины.
 */
export function resolveColumns(input: ResolveInput): ResolvedColumns {
  const { mode, hasPanel, stored, thresholds, containerWidth } = input;
  const want = (key: BuilderColumnKey) => stored[key] ?? defaultColumnWidth(key, thresholds);
  const isCollapsed = (key: BuilderColumnKey) => stored[collapsedFlag(key)] === true;
  const strip = (key: BuilderColumnKey): ResolvedColumn => ({ width: COLLAPSED_PX, ...COLUMN_LIMITS[key], collapsed: true });
  const room = (taken: number) => (containerWidth === null ? Infinity : containerWidth - CANVAS_MIN[mode] - taken);

  if (mode === "tablet") {
    if (isCollapsed("side")) return { side: strip("side") };
    const lim = COLUMN_LIMITS.side;
    const budget = room(HANDLE_PX);
    const width = Math.max(lim.min, Math.min(want("side"), budget));
    return { side: { width, min: lim.min, max: Math.max(width, Math.min(lim.max, budget)), collapsed: false } };
  }

  const navOpen = !isCollapsed("nav");
  const panelOpen = hasPanel && !isCollapsed("panel");
  // Место под развёрнутые колонки: минус канва, ручки и свёрнутые полоски.
  const taken =
    (navOpen ? HANDLE_PX : COLLAPSED_PX) + (hasPanel ? (panelOpen ? HANDLE_PX : COLLAPSED_PX) : 0);
  const budget = room(taken);
  let nav = navOpen ? want("nav") : 0;
  let panel = panelOpen ? want("panel") : 0;
  let over = nav + panel - budget;
  if (over > 0 && panelOpen) {
    panel -= Math.max(0, Math.min(over, panel - COLUMN_LIMITS.panel.min));
    over = nav + panel - budget;
  }
  if (over > 0 && navOpen) nav -= Math.max(0, Math.min(over, nav - COLUMN_LIMITS.nav.min));

  const open = (key: "nav" | "panel", width: number, otherMin: number): ResolvedColumn => ({
    width,
    min: COLUMN_LIMITS[key].min,
    max: Math.max(width, Math.min(COLUMN_LIMITS[key].max, budget - otherMin)),
    collapsed: false,
  });
  const minOf = (key: "nav" | "panel", isOpen: boolean) => (isOpen ? COLUMN_LIMITS[key].min : 0);
  const out: ResolvedColumns = { nav: navOpen ? open("nav", nav, minOf("panel", panelOpen)) : strip("nav") };
  if (hasPanel) out.panel = panelOpen ? open("panel", panel, minOf("nav", navOpen)) : strip("panel");
  return out;
}

/**
 * Соседняя колонка, которую надо ужать, чтобы колонке `key` досталось `px`
 * без залезания в минимум канвы (от 1280: тянем навигатор шире — уже панель,
 * и наоборот, но не меньше её минимума). `null` — места хватает.
 */
export function neighborFor(
  columns: ResolvedColumns,
  key: BuilderColumnKey,
  px: number,
  containerWidth: number | null,
): { key: BuilderColumnKey; width: number } | null {
  if (containerWidth === null || key === "side") return null;
  const otherKey: BuilderColumnKey = key === "nav" ? "panel" : "nav";
  const other = columns[otherKey];
  if (!other || other.collapsed) return null;
  const taken = Object.values(columns).reduce((sum, c) => sum + (c.collapsed ? COLLAPSED_PX : HANDLE_PX), 0);
  const free = containerWidth - CANVAS_MIN.desktop - taken - px;
  return other.width > free ? { key: otherKey, width: Math.max(other.min, free) } : null;
}

/**
 * Шаблон CSS-сетки: колонки, ручки у развёрнутых колонок, канва между ними.
 * Ширины развёрнутых — CSS-переменными (`--builder-nav` …), чтобы тяга
 * меняла их без перерисовки.
 */
export function gridTemplate(columns: ResolvedColumns): string {
  const track = (key: BuilderColumnKey) => {
    const col = columns[key];
    if (!col) return [];
    return col.collapsed ? [`${COLLAPSED_PX}px`] : [`var(--builder-${key})`, `${HANDLE_PX}px`];
  };
  const left = [...track("nav"), ...track("side")];
  const right = track("panel").reverse();
  return [...left, "minmax(0,1fr)", ...right].join(" ");
}
